import React from "react";
import AutoCompleteDialog from "./AutoCompleteDialog";
import { useAutoCompleteDialog } from "../hooks/useAutoCompleteDialog";
import Flex from "./Generic/Flex";
import Question from "./OnboardingSection/Question";
import { translate } from "../translate";
import { STRINGS } from "../strings";
import styled from "@emotion/styled";

const MAX_ANSWERS_TO_SHOW = 5;

function answerAsText(answer: string | string[] | null | undefined) {
  if (typeof answer === "string") {
    return answer;
  }
  if (Array.isArray(answer) && answer.length > 0) {
    return answer.length <= MAX_ANSWERS_TO_SHOW
      ? answer.slice(0, MAX_ANSWERS_TO_SHOW).join(", ")
      : `${answer.length} ${translate(STRINGS.SELECTED)}`;
  }
  return translate(STRINGS.SELECT_FROM_LIST);
}

export function RequiredDocAutocompleteCheck({
  handleCheck,
  inAnswer,
  possibleAnswers,
  readonly,
  currentAnswer,
}: {
  currentAnswer?: string | string[] | null | undefined;
  readonly: boolean;
  possibleAnswers: string[];
  handleCheck: (value: string, checked: boolean) => void;
  inAnswer: (value: string) => boolean;
}) {
  const { close, isOpen, open, query, setQuery } = useAutoCompleteDialog();

  const answerText = answerAsText(currentAnswer);

  const predictions = React.useMemo(() => {
    return [...possibleAnswers]
      .sort((a, b) => (inAnswer(a) === inAnswer(b) ? 0 : inAnswer(a) ? -1 : 1))
      .filter((x) => (query.length < 1 ? true : x.toLowerCase().includes(query.toLowerCase())))
      .map((answer) => {
        const checked = inAnswer(answer);
        return {
          id: answer,
          title: answer,
          checked,
          value: answer,
          onClick: () => handleCheck(answer, !checked),
          renderLeading: () => (
            <Leading className="checkbox checkbox--material">
              <input
                className="checkbox__input checkbox--material__input"
                readOnly
                type="checkbox"
                checked={checked}
              />
              <div className="checkbox__checkmark checkbox--material__checkmark" />
            </Leading>
          ),
        };
      });
  }, [handleCheck, inAnswer, possibleAnswers, query]);

  return (
    <Flex direction="column" gap={8}>
      <Question.Input
        placeholder={answerText}
        onClick={() => !readonly && open()}
        onFocus={() => !readonly && open()}
      />
      <AutoCompleteDialog
        isLoading={false}
        isOpen={isOpen}
        onChange={setQuery}
        onClickPrediction={(prediction) => {
          handleCheck(prediction.id, !prediction.checked);
          setQuery("");
        }}
        onRequestClose={close}
        placeholder={translate(STRINGS.SEARCH)}
        predictions={predictions}
        query={query}
        showPredictions={true}
      />
    </Flex>
  );
}

const Leading = styled.span`
  padding-right: 8px;
`;
